import { useAuth, useLocalStorage } from "@elit-hair/elit-tools";
import { DatePicker, Icon, SectionHeading } from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { t } from "i18next";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { TbCalendar, TbChartBar } from "react-icons/tb";
import z from "zod";

import { CallsTimeline } from "@/components/charts/calls-timeline";
import { KpiChartType } from "@/components/charts/chart-config";
import { DateSelectionCards } from "@/components/charts/date-selection-cards";
import { KpiChart } from "@/components/charts/kpi-chart";
import { PerformanceSummary } from "@/components/charts/performance-summary";
import { TeamMemberSelector } from "@/components/ui/team-member-selector";
import { LOCAL_STORAGE_KEYS } from "@/constants/local-storage-keys";
import { useCallsForAnalytics } from "@/hooks/queries/call.queries";
import { useUserKpiByDateRange } from "@/hooks/queries/kpi.queries";
import { useMediaQuery } from "@/hooks/use-media-query";
import { KpiMobileDayCarousel } from "@/pages/performance/components/kpi-mobile-day-carousel";
import { KpiData } from "@/types/call.types";
import { getDateString, getLastNDates } from "@/utils/format-date";
import { transformKpiToCombinedHourlyChartData } from "@/utils/kpi.utils";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";

const KpisQueryParamsSchema = z.object({
    agent: z.string().optional(),
});

const EMPTY_KPI_DATA: Record<string, KpiData> = {};

export const Route = createFileRoute(APP_ROUTES_FULL.PERFORMANCE_KPIS)({
    component: PerformanceKpis,
    validateSearch: zodValidator(KpisQueryParamsSchema),
    staticData: {
        config: {
            breadcrumb: {
                title: "kpis",
                to: APP_ROUTES_TO.PERFORMANCE_KPIS,
            },
            title: "kpis",
        },
    },
});

function PerformanceKpis() {
    const { userData } = useAuth();
    const searchParams = Route.useSearch();
    const navigate = Route.useNavigate();

    const [, setLastSelectedAgent] = useLocalStorage<string | null>(
        LOCAL_STORAGE_KEYS.SELECTED_AGENT_KPI.key,
        LOCAL_STORAGE_KEYS.SELECTED_AGENT_KPI.default
    );

    const currentUserId = userData?.id ?? null;
    const isViewingOwnKpis = !searchParams.agent || searchParams.agent === currentUserId;
    const selectedMember = searchParams.agent ?? currentUserId;
    const selectorMemberId = isViewingOwnKpis ? null : (searchParams.agent ?? null);

    const handleAgentChange = (agentId: string | null) => {
        const isOwnKpis = !agentId || agentId === currentUserId;

        if (isOwnKpis) {
            navigate({ to: Route.to, search: {}, replace: true });
            return;
        }

        navigate({ to: Route.to, search: { agent: agentId } });
        setLastSelectedAgent(agentId);
    };

    const isMobile = !useMediaQuery("(min-width: 768px)");

    const today = getDateString(DateTime.now());
    const [selectedDay, setSelectedDay] = useState<string>(today);
    const [fixedWeekEnd, setFixedWeekEnd] = useState<string>(today);

    const days = useMemo(() => getLastNDates(fixedWeekEnd, 7), [fixedWeekEnd]);

    const rangeStart = useMemo(() => DateTime.fromISO(days[0]), [days]);
    const rangeEnd = useMemo(() => DateTime.fromISO(days[days.length - 1]), [days]);

    const { data: memberData = EMPTY_KPI_DATA } = useUserKpiByDateRange(rangeStart, rangeEnd, selectedMember);

    const { data: dayCalls = [] } = useCallsForAnalytics(selectedDay, selectedMember ?? undefined);

    const handleDateChange = useCallback(
        (date: Date | undefined) => {
            if (date) {
                setSelectedDay(getDateString(DateTime.fromJSDate(date)));
                setFixedWeekEnd(getDateString(DateTime.fromJSDate(date)));
            }
        },
        [setSelectedDay, setFixedWeekEnd]
    );

    const handleChartIconClick = useCallback(
        (date: string) => {
            setSelectedDay(date);
        },
        [setSelectedDay]
    );

    const memberHourlyCallData = useMemo(
        () => transformKpiToCombinedHourlyChartData(memberData[selectedDay]),
        [memberData, selectedDay]
    );

    return (
        <div className="flex flex-col gap-4 p-2 h-full w-full relative">
            <div className="absolute top-0 right-2 flex items-center gap-2">
                <DatePicker date={selectedDay ? new Date(selectedDay) : new Date()} onDateChange={handleDateChange} />
            </div>

            <div className="h-full w-full">
                <div className="space-y-8 pt-6">
                    <div className="flex flex-col gap-6">
                        <div className="w-full max-w-lg">
                            <TeamMemberSelector
                                selectedMemberId={selectorMemberId}
                                onMemberChange={handleAgentChange}
                                currentUserId={currentUserId}
                            />
                        </div>
                    </div>

                    {selectedMember && (
                        <>
                            {isMobile && (
                                <KpiMobileDayCarousel
                                    days={days}
                                    selectedDay={selectedDay}
                                    onDateSelect={handleChartIconClick}
                                    data={memberData}
                                />
                            )}

                            <div className="hidden space-y-8 md:block">
                                <div>
                                    <SectionHeading
                                        title={t("daily_overview")}
                                        icon={<Icon icon={TbCalendar} size={20} />}
                                        color="blue"
                                    />
                                    <DateSelectionCards
                                        days={days}
                                        selectedDay={selectedDay}
                                        onDateSelect={handleChartIconClick}
                                        data={memberData}
                                        showData={true}
                                    />
                                </div>

                                <PerformanceSummary kpiData={memberData?.[selectedDay] || null} />

                                <div>
                                    <SectionHeading
                                        title={t("hourly_analytics")}
                                        icon={<Icon icon={TbChartBar} size={20} />}
                                        color="purple"
                                    />
                                    <div className="w-full flex flex-col gap-4">
                                        <KpiChart
                                            data={memberHourlyCallData}
                                            type={KpiChartType.COMBINED_METRICS}
                                            height={400}
                                        />
                                        <CallsTimeline calls={dayCalls} />
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
